/**
 * AppShell — root layout wrapper.
 *
 * Category: layout (composition — slots, responsive chrome)
 * Loads eagerly. All page content loads lazily via the router outlet.
 *
 * Structure: AppShell -> SectionLayout -> PageContent
 * Renders: header (eager) + main content area (lazy via <Outlet />)
 *
 * @spec SPEC:ENGINEERING:FRONTEND:LAYOUT
 */
import { useState, useEffect } from 'react'
import { Outlet, Link, useLocation } from '@tanstack/react-router'
import { LogOut, Menu, MessageSquarePlus, X } from 'lucide-react'
import { useAuth } from '@/context/AuthContext'
import { useCreditsContext } from '@/context/CreditsContext'
import { useOverlay } from '@/context/OverlayContext'
import { useCredits } from '@/queries/useCredits'
import { CreditPurchaseModal } from '@/components/CreditPurchaseModal'
import { FeedbackModal } from '@/components/FeedbackModal'
import { APP_NAME } from '@/lib/constants'

export function AppShell() {
  const { user, isAuthenticated, logout } = useAuth()
  const { data: credits } = useCredits()
  const { openPurchaseModal, isOpen, fromOutOfCredits, close } = useCreditsContext()
  const { isVisible: overlayVisible, message: overlayMessage } = useOverlay()
  const [feedbackOpen, setFeedbackOpen] = useState(false)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const location = useLocation()

  // Close drawer on route change
  useEffect(() => {
    setDrawerOpen(false)
  }, [location.pathname])

  // Lock body scroll when drawer is open
  useEffect(() => {
    if (drawerOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [drawerOpen])

  return (
    <div className="flex min-h-screen flex-col bg-background">
      {/* -- Header (always loaded eagerly) -- */}
      <header className="sticky top-0 z-[--z-raised] border-b border-border bg-background">
        <div className="mx-auto flex h-14 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-6">
            <Link to="/" className="flex items-center gap-2 text-lg font-bold text-foreground hover:text-foreground/80">
              <img src="/rs-logo.png" alt="" className="h-7 w-7" />
              {APP_NAME}
            </Link>
            {isAuthenticated && (
              <nav aria-label="Main navigation" className="flex items-center gap-1">
                <Link
                  to="/projects"
                  className="relative px-3 py-1 text-sm text-muted-foreground hover:text-foreground transition-colors rounded-md hover:bg-muted"
                  activeProps={{ className: 'relative px-3 py-1 text-sm text-foreground font-medium rounded-md after:absolute after:bottom-[-13px] after:left-2 after:right-2 after:h-[2px] after:bg-primary after:rounded-full' }}
                >
                  Projects
                </Link>
                {user?.role === 'admin' && (
                  <Link
                    to="/admin/users"
                    className="relative px-3 py-1 text-sm text-muted-foreground/60 hover:text-foreground transition-colors rounded-md hover:bg-muted"
                    activeProps={{ className: 'relative px-3 py-1 text-sm text-foreground/70 font-medium rounded-md after:absolute after:bottom-[-13px] after:left-2 after:right-2 after:h-[2px] after:bg-primary/50 after:rounded-full' }}
                  >
                    Admin
                  </Link>
                )}
              </nav>
            )}
          </div>
          {/* Desktop right-side items — hidden below md */}
          <div className="hidden md:block">
            {isAuthenticated && user ? (
              <div className="flex items-center gap-3">
                {/* Feedback button */}
                <button
                  onClick={() => setFeedbackOpen(true)}
                  className="flex items-center gap-1.5 rounded-md px-2.5 py-1 text-sm text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                >
                  <MessageSquarePlus className="h-4 w-4" />
                  Feedback
                </button>

                <div className="h-5 w-px bg-border" aria-hidden="true" />

                {/* Credits group */}
                {credits !== undefined && (
                  <div className="flex items-center gap-0 rounded-lg border border-border">
                    <span className="px-3 py-1 text-sm tabular-nums text-muted-foreground">
                      {credits.balance.toLocaleString()} credits
                    </span>
                    <button
                      onClick={() => openPurchaseModal(false)}
                      className="rounded-r-[7px] border-l border-border bg-primary px-3 py-1 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
                    >
                      Buy credits
                    </button>
                  </div>
                )}

                <div className="h-5 w-px bg-border" aria-hidden="true" />

                {/* Account pill */}
                <Link
                  to="/account"
                  className="flex items-center gap-2 rounded-full border border-border px-1 py-0.5 pr-3 hover:bg-muted transition-colors"
                >
                  {user.avatarUrl ? (
                    <img
                      src={user.avatarUrl}
                      alt=""
                      className="h-6 w-6 rounded-full"
                    />
                  ) : (
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-muted text-xs font-medium text-muted-foreground">
                      {user.name.charAt(0).toUpperCase()}
                    </span>
                  )}
                  <span className="text-sm text-foreground">{user.name}</span>
                </Link>

                <div className="h-5 w-px bg-border" aria-hidden="true" />

                {/* Sign out */}
                <button
                  onClick={() => void logout()}
                  className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                  title="Sign out"
                >
                  <LogOut className="h-4 w-4" />
                </button>
              </div>
            ) : null}
          </div>

          {/* Mobile hamburger button — visible below md */}
          {isAuthenticated && user && (
            <button
              onClick={() => setDrawerOpen(true)}
              className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors md:hidden"
              aria-label="Open menu"
            >
              <Menu className="h-5 w-5" />
            </button>
          )}

        </div>
      </header>

      {/* Mobile drawer overlay — rendered outside header to avoid flex/z-index issues */}
      {isAuthenticated && user && drawerOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setDrawerOpen(false)}
          />

          {/* Drawer panel */}
          <div className="absolute right-0 top-0 h-full w-72 bg-white dark:bg-slate-950 shadow-xl border-l border-border">
            {/* Close button */}
            <div className="flex items-center justify-end border-b border-border px-4 h-14">
              <button
                onClick={() => setDrawerOpen(false)}
                className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                aria-label="Close menu"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Drawer content */}
            <div className="flex flex-col gap-1 p-4">
              {/* Feedback button */}
              <button
                onClick={() => { setFeedbackOpen(true); setDrawerOpen(false) }}
                className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
              >
                <MessageSquarePlus className="h-4 w-4" />
                Feedback
              </button>

              <div className="my-1 h-px bg-border" aria-hidden="true" />

              {/* Credits group */}
              {credits !== undefined && (
                <>
                  <div className="flex items-center justify-between rounded-md px-3 py-2">
                    <span className="text-sm tabular-nums text-muted-foreground">
                      {credits.balance.toLocaleString()} credits
                    </span>
                    <button
                      onClick={() => { openPurchaseModal(false); setDrawerOpen(false) }}
                      className="rounded-md bg-primary px-3 py-1 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
                    >
                      Buy credits
                    </button>
                  </div>

                  <div className="my-1 h-px bg-border" aria-hidden="true" />
                </>
              )}

              {/* Account */}
              <Link
                to="/account"
                className="flex items-center gap-2 rounded-md px-3 py-2 hover:bg-muted transition-colors"
              >
                {user.avatarUrl ? (
                  <img
                    src={user.avatarUrl}
                    alt=""
                    className="h-6 w-6 rounded-full"
                  />
                ) : (
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-muted text-xs font-medium text-muted-foreground">
                    {user.name.charAt(0).toUpperCase()}
                  </span>
                )}
                <span className="text-sm text-foreground">{user.name}</span>
              </Link>

              <div className="my-1 h-px bg-border" aria-hidden="true" />

              {/* Sign out */}
              <button
                onClick={() => void logout()}
                className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
              >
                <LogOut className="h-4 w-4" />
                Sign out
              </button>
            </div>
          </div>
        </div>
      )}

      {/* -- Main content (lazy-loaded pages via router) -- */}
      <main id="main-content" className="relative flex-1" tabIndex={-1}>
        {overlayVisible && (
          <div className="absolute inset-0 z-40 flex flex-col items-center gap-4 bg-background pt-32">
            <div
              className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"
              aria-hidden="true"
            />
            <p className="text-sm font-medium text-muted-foreground">{overlayMessage}</p>
          </div>
        )}
        <Outlet />
      </main>

      {/* -- Footer -- */}
      <footer className="border-t border-border py-4">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-4 text-sm text-muted-foreground">
            <Link to="/privacy" className="underline underline-offset-4 hover:text-foreground">
              Privacy
            </Link>
            <span aria-hidden="true">·</span>
            <Link to="/terms" className="underline underline-offset-4 hover:text-foreground">
              Terms
            </Link>
            <span aria-hidden="true">·</span>
            <a href="mailto:support@rocketships.ai" className="underline underline-offset-4 hover:text-foreground">
              Contact Support
            </a>
          </div>
        </div>
      </footer>

      {/* Feedback modal — local state, no context needed */}
      <FeedbackModal open={feedbackOpen} onOpenChange={setFeedbackOpen} />

      {/* Credit purchase modal — rendered once here, controlled by CreditsContext */}
      <CreditPurchaseModal
        open={isOpen}
        onOpenChange={(open) => { if (!open) close() }}
        showOutOfCreditsMessage={fromOutOfCredits}
      />
    </div>
  )
}
